'use client';
import { Box, Card, CardContent, Chip, Typography } from '@mui/material';

import { useT } from '@/hooks';
import type { CommentData } from '@/types';
import { formatRelativeTime, formatShares } from '@/utils';

interface CommentItemProps {
  comment: CommentData;
}

export function CommentItem({ comment }: CommentItemProps) {
  const { t } = useT();
  const { user, createdAt, comment: content } = comment;

  let authorName = 'Anonymous';
  if (user?.displayUsernamePublic) {
    authorName = user.name || user.username || 'Anonymous';
  }

  let shareCount: number | null = null;
  if (user?.positions?.length) {
    const largest = user.positions.reduce((best, pos) =>
      Number(pos.positionSize) > Number(best.positionSize) ? pos : best,
    );
    const shares = formatShares(largest.positionSize);
    if (shares > 0) {
      shareCount = shares;
    }
  }

  const time = formatRelativeTime(createdAt);

  return (
    <Card
      variant="outlined"
      sx={{
        bgcolor: 'action.hover',
        width: '100%',
        maxWidth: { xs: '100%', sm: 445, md: '100%' },
      }}
    >
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography
            component="span"
            variant="body2"
            fontWeight={600}
            sx={{
              maxWidth: { xs: 120, sm: 150, md: 200 },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {authorName}
          </Typography>
          {shareCount !== null && (
            <Chip
              label={t('market.sharesCount', {
                count: shareCount,
                defaultValue: '{{count}} shares',
              })}
              size="small"
              color="primary"
              sx={{ height: 20, fontSize: '0.625rem' }}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {time}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {content}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default CommentItem;
